import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import DailoqaLogo from './assets/dailoqa_logo.png';
import BroccoliText from './assets/broccoli_text.png';
import BroccoliCharacter from './assets/broccoli_character.png';

// Mascot positions for desktop and mobile
const floatingMascots = [
  { style: 'hidden md:block top-[15%] left-[50%] w-10 sm:w-14', key: 'd1' },
  { style: 'hidden md:block top-[25%] left-[80%] w-12 sm:w-16', key: 'd2' },
  { style: 'hidden md:block top-[45%] left-[90%] w-11 sm:w-14', key: 'd3' },
  { style: 'hidden md:block bottom-10 left-[70%] w-12 sm:w-16', key: 'd4' },
  { style: 'hidden md:block bottom-[10%] left-[45%] w-10 sm:w-14', key: 'd5' },
  { style: 'hidden md:block bottom-[35%] left-[15%] w-12 sm:w-16', key: 'd6' },
  { style: 'hidden md:block top-[15%] left-[10%] w-11 sm:w-14', key: 'd7' },
  { style: 'block md:hidden top-2 left-1/2 -translate-x-1/2 w-10', key: 'm1' },
  { style: 'block md:hidden top-20 left-6 w-10', key: 'm2' },
  { style: 'block md:hidden top-24 right-6 w-10', key: 'm3' },
  { style: 'block md:hidden bottom-20 left-10 w-10', key: 'm4' },
  { style: 'block md:hidden bottom-10 right-10 w-10', key: 'm5' },
  { style: 'block md:hidden bottom-4 left-1/2 -translate-x-1/2 w-10', key: 'm6' },
  { style: 'block md:hidden top-1/2 left-1/2 -translate-x-1/2 w-10', key: 'm7' },
];

const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, checkAuthStatus } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState('');

  // Redirect on auth change
  useEffect(() => {
    if (process.env.REACT_APP_SKIP_AUTH === 'true') {
      navigate('/studio/tools', { replace: true });
      return;
    }
    if (!isLoading && isAuthenticated) {
      navigate('/studio/tools', { replace: true });
    }
  }, [isLoading, isAuthenticated, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setIsSubmitting(true);
    if (!username || !password) {
      setLoginError('Username and password are required.');
      setIsSubmitting(false);
      return;
    }
    try {
      const { authService } = await import('./services/authService');
      const result = await authService.signIn({ username, password });
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setLoginError('A new password is required. Please reset your password.');
        setIsSubmitting(false);
        return;
      }
      if (result.isSignedIn) {
        const ok = await checkAuthStatus();
        if (ok) {
          navigate('/studio/tools', { replace: true });
          return;
        } else {
          setLoginError('Failed to update authentication state. Please try again.');
        }
      }
    } catch (err: any) {
      setLoginError(err.message || 'Login failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    
    <div className="min-h-screen flex flex-col md:flex-row bg-white">
      {/* Left: Login */}
      <div className="flex flex-col md:flex-1 w-full md:w-1/2 bg-white relative">
        {/* Logo */}
        <div className="absolute top-6 left-6 md:static md:mt-8 md:ml-12">
          <img src={DailoqaLogo} alt="Dailoqa Logo" className="w-40 md:w-48" />
        </div>
        {/* Centered content starts below logo on mobile */}
        <div className="flex flex-1 flex-col justify-start md:justify-center items-center px-4 md:px-0 w-full pt-20 md:pt-0">
          {/* Heading moved outside form/card */}
          <h2 className="text-2xl sm:text-2xl md:text-2xl font-bold text-black mb-6 text-center w-full px-4 md:px-0">
            Unlocking Combined Intelligence
          </h2>
          <div className="w-full flex justify-center items-center">
            <div className="bg-white rounded-2xl shadow-2xl px-6 md:px-10 py-8 min-w-[90vw] max-w-[400px] w-full md:min-w-[350px]">
              <h3 className="text-2xl font-bold text-center text-blue-700 mb-1">Welcome</h3>
              <p className="text-center text-gray-700 mb-6">Sign in to your account</p>
              {loginError && (
                <div className="bg-red-50 text-red-500 p-3 rounded-md text-sm mb-2 text-center">
                  {loginError}
                </div>
              )}
              <form className="space-y-4" onSubmit={handleLogin}>
                <div>
                  <label htmlFor="username" className="block text-sm font-medium text-gray-700">Username</label>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    autoComplete="username"
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base bg-gray-50"
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base bg-gray-50"
                    disabled={isSubmitting}
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-2 px-4 rounded-md text-white font-semibold text-lg bg-gradient-to-r from-blue-700 to-indigo-500 shadow-md hover:from-blue-800 hover:to-indigo-600 transition ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isSubmitting ? 'Signing in...' : 'Sign in'}
                </button>
              </form>
              <a href="/forgot-password" className="block text-center text-blue-600 text-sm mt-3 hover:underline">Forgot your password?</a>
            </div>
          </div>
        </div>
      </div>
      {/* Right: Branding */}
      <div className="flex-1 w-full md:w-1/2 bg-[#f3f3fc] relative flex flex-col items-center justify-center overflow-hidden min-h-[350px]">
        {/* Floating mascots */}
        {floatingMascots.map(m => (
          <img
            key={m.key}
            src={BroccoliCharacter}
            alt="Broccoli Mascot"
            className={`absolute ${m.style} opacity-95 animate-float pointer-events-none select-none`}
          />
        ))}
        <div className="flex flex-col items-center mt-8 z-10">
          <span className="text-gray-700 text-lg mb-2">Powered by <b>Dailoqa's</b></span>
          <img src={BroccoliText} alt="Broccoli" className="w-[220px] sm:w-[320px] max-w-full mb-8" />
        </div>
        <span className="absolute bottom-8 left-1/2 -translate-x-1/2 text-gray-500 text-base z-10">©2025 Dailoqa</span>
      </div>
      
      <style>{`
        @keyframes float {
          0% { transform: translateY(0px) rotate(-5deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
          100% { transform: translateY(0px) rotate(-5deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default LoginForm;
